/**
 * Triangle Navigation Constants
 * Exact coordinates extracted from Figma SVGs
 * ViewBox: 0 0 247 242
 */

// Triangle path (rounded bezier curve from Figma)
export const TRIANGLE_PATH =
  'M132.153 59.9412C128.301 53.2902 118.699 53.2902 114.847 59.9412L62.6943 149.988C58.8332 156.655 63.6437 165 71.3478 165H175.652C183.356 165 188.167 156.655 184.306 149.988L132.153 59.9412Z'

// Circle sizes (exact from Figma)
export const SIZES = {
  centerCircle: 25, // Large center circle radius
  peripheralCircle: 7, // Small peripheral circle radius
  dimensionCircle: 12, // Dimension circles on start state
  activeCircle: 25, // Active/hovered circle radius
}

// Center circle positions for each dimension (from Figma SVGs)
export const CENTER_POSITIONS = {
  start: {
    cx: 123.5,
    cy: 125,
  },
  'barnets-utvikling': {
    // Left dimension - base position
    cx: 89,
    cy: 104,
  },
  'foreldrenes-omsorg': {
    // Right dimension - base position
    cx: 158,
    cy: 104,
  },
  'familie-og-miljo': {
    // Bottom dimension - base position
    cx: 124,
    cy: 165,
  },
}

// Area-specific center circle positions ON triangle edges
// Small offsets from base dimension positions to move along the edge
// Base positions: left (89, 104), right (158, 104), bottom (124, 165)
export const AREA_CENTER_OFFSETS = {
  'barnets-utvikling': [
    // Left edge - subtle movement along diagonal
    { cx: 98, cy: 88 }, // Area 0 - move up/right along edge
    { cx: 94, cy: 96 }, // Area 1 - upper-middle
    { cx: 84, cy: 112 }, // Area 2 - lower-middle
    { cx: 79, cy: 120 }, // Area 3 - move down/left along edge
  ],
  'foreldrenes-omsorg': [
    // Right edge - subtle movement along diagonal
    { cx: 148, cy: 88 }, // Area 0 - move up/left along edge
    { cx: 153, cy: 96 }, // Area 1 - upper-middle
    { cx: 162, cy: 112 }, // Area 2 - lower-middle
    { cx: 165, cy: 120 }, // Area 3 - move down/right along edge
  ],
  'familie-og-miljo': [
    // Bottom edge - horizontal movement
    { cx: 108, cy: 164 }, // Area 0 - left
    { cx: 124, cy: 164 }, // Area 1 - center (base position)
    { cx: 140, cy: 164 }, // Area 2 - right
  ],
}

// Start state: 3 dimension circles (r=12, exact from Figma)
export const START_CIRCLES = [
  {
    id: 'dimension-barnets-utvikling',
    handle: 'barnets-utvikling',
    type: 'dimension',
    cx: 54,
    cy: 82,
  },
  {
    id: 'dimension-foreldrenes-omsorg',
    handle: 'foreldrenes-omsorg',
    type: 'dimension',
    cx: 193,
    cy: 82,
  },
  {
    id: 'dimension-familie-og-miljo',
    handle: 'familie-og-miljo',
    type: 'dimension',
    cx: 124,
    cy: 200,
  },
]

// All 11 peripheral area circles (exact from Figma, r=7 normally, r=25 when active)
// Ordered by dimension: [0-3] left side, [4-7] right side, [8-10] bottom
export const PERIPHERAL_CIRCLES = [
  // Left side - Barnets utvikling (4 circles, top to bottom)
  { id: 'area-0', cx: 90, cy: 24 }, // Top-left
  { id: 'area-1', cx: 51, cy: 50 }, // Upper-left
  { id: 'area-2', cx: 29, cy: 89 }, // Middle-left
  { id: 'area-3', cx: 24, cy: 136 }, // Lower-left

  // Right side - Foreldrenes omsorg (4 circles, top to bottom)
  { id: 'area-4', cx: 158, cy: 24 }, // Top-right
  { id: 'area-5', cx: 197, cy: 50 }, // Upper-right
  { id: 'area-6', cx: 218, cy: 89 }, // Middle-right
  { id: 'area-7', cx: 223, cy: 136 }, // Lower-right

  // Bottom - Familie og miljø (3 circles, left to right)
  { id: 'area-8', cx: 74, cy: 205 }, // Bottom-left
  { id: 'area-9', cx: 124, cy: 218 }, // Bottom-center
  { id: 'area-10', cx: 174, cy: 205 }, // Bottom-right
]

// Map dimension handles to their circle index ranges
export const CIRCLE_RANGES = {
  'barnets-utvikling': { start: 0, end: 3 }, // 4 circles (indices 0-3)
  'foreldrenes-omsorg': { start: 4, end: 7 }, // 4 circles (indices 4-7)
  'familie-og-miljo': { start: 8, end: 10 }, // 3 circles (indices 8-10)
}

// Colors from design system
export const COLORS = {
  triangleFill: '#350F4B', // Dark purple
  circleFillInactive: 'transparent',
  circleFillActive: '#350F4B', // Dark purple
  circleStroke: '#350F4B', // Dark purple
  centerCircleFill: '#E5D8FF', // Light purple
  centerCircleMasked: '#FFFEF9', // Off-white
  background: '#E5D8FF', // Light purple background
}

// Animation durations and settings
export const ANIMATION = {
  centerMoveDuration: 0.8,
  circleStaggerDelay: 0.05,
  circleAppearDuration: 0.4,
  hoverScale: 1.1, // Subtle hover effect (circles are already correct size via r attribute)
  hoverRadiusScale: 25 / 7, // Scale from r=7 to r=25 for hover effect on peripheral circles
  buttonFadeDuration: 0.3,
  // Entry animation (first page load)
  entrySpinDuration: 0.5,
  entryCenterDelay: 0.1,
  entryCircleDelay: 0.2,
}

// State types
export const STATES = {
  START: 'start',
  DIMENSION: 'dimension',
  AREA: 'area',
}
